// The demo half of the family-licence, billing, kids-management and owner-console
// contract (migration 20261022000008), behind the fixtures adapter.
//
// STATEFUL ON PURPOSE. A reviewer giving a licence, ending one, moving a child to
// Inactive or issuing an invoice should see the screen change, so these stores
// are mutable for the life of the page. They hold adult emails, opaque child
// refs and counts only — exactly what the live RPCs return — never a child id
// or a progress figure.
//
// Three demo organisations, switched by ./demoVariant: a centre with licences, a
// single practitioner with NO licences (operating without them is first-class),
// and a centre Zooylo has suspended.
//
// Dates are built from the real clock at module load, rounded to the day, so
// "ends in 18 days" stays true whenever the demo is opened.

import {
  accounts,
  assignments as fixtureAssignments,
  childLinks,
  homeNotes as fixtureHomeNotes,
  organisation,
} from "../domain/fixtures";
import { normaliseNote, SUGGESTION_GAMES_MAX } from "../domain/assignmentNote";
import { PortalWriteError } from "./supabaseAdapter";
import { BUILT_GAMES } from "../progress/skillRegistry.generated";
import { isValidEmail } from "../domain/bulkEmails";
import type { LinkRole } from "../domain/types";
import type { Sponsorship } from "../domain/sponsorship";
import { countsTowardAllowance } from "../domain/sponsorship";
import { getDemoVariant } from "./demoVariant";
import { children as fixtureChildren } from "../domain/fixtures";
import { FIXTURE_ACCOUNT_ID } from "../app/caregiver/data";
import type {
  AccessCode,
  BillingSummary,
  BulkLicenceResult,
  ChildAccess,
  ChildAdult,
  ChildActivity,
  ChildLicence,
  ChildSuggestion,
  ConsoleOrg,
  FamilyLicenceCheck,
  Invoice,
  OrgAnalytics,
  OrgChildEntry,
  OrgChildStaff,
  OrgProfile,
  PortalDataSource,
  SharingStatus,
} from "./types";

const DAY = 24 * 60 * 60 * 1000;
/** Today at 00:00 UTC — the demo's "now". */
export const DEMO_NOW_MS = Math.floor(Date.now() / DAY) * DAY;
const at = (days: number) => new Date(DEMO_NOW_MS + days * DAY).toISOString();
const day = (days: number) => at(days).slice(0, 10);

/** Deterministic stand-in for the server's md5 slice. Same shape, no crypto. */
export function fixtureChildRef(childId: string): string {
  let h = 2166136261;
  for (let i = 0; i < childId.length; i++) {
    h ^= childId.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).toUpperCase().padStart(6, "0").slice(0, 6);
}

/** Emails the demo treats as already paying for Premium in the app store. */
export const DEMO_PAID_EMAILS = ["siti.rahman@example.com"] as const;

export const DEMO_ORG_ID = "org-bright-steps";
const INDIVIDUAL_ORG_ID = "org-dr-aisyah";
const SUSPENDED_ORG_ID = "org-little-lanterns";

// --- Family licences ---------------------------------------------------------

const centreLicences: Sponsorship[] = [
  { id: "lic-pending", email: "hana.rahman@example.com", endsAt: at(250), grantedAt: at(-6), claimedAt: null, revokedAt: null },
  { id: "lic-active", email: "priya.menon@example.com", endsAt: at(200), grantedAt: at(-80), claimedAt: at(-79), revokedAt: null },
  { id: "lic-revoked", email: "daniel.osei@example.com", endsAt: at(150), grantedAt: at(-120), claimedAt: at(-118), revokedAt: at(-40) },
  { id: "lic-lapsed", email: "aminah.yusof@example.com", endsAt: at(-12), grantedAt: at(-200), claimedAt: at(-199), revokedAt: null },
  { id: "lic-ending", email: "wei.ling.tan@example.com", endsAt: at(18), grantedAt: at(-330), claimedAt: at(-329), revokedAt: null },
];
const individualLicences: Sponsorship[] = [];

export function demoLicences(): Sponsorship[] {
  return getDemoVariant() === "individual" ? individualLicences : centreLicences;
}

export function demoAllowance(): number {
  return getDemoVariant() === "individual" ? 0 : 5;
}

function demoPrice(): number {
  return getDemoVariant() === "individual" ? 0 : 30;
}

function demoOrgId(): string {
  const v = getDemoVariant();
  return v === "individual" ? INDIVIDUAL_ORG_ID : v === "suspended" ? SUSPENDED_ORG_ID : DEMO_ORG_ID;
}

const live = () => demoLicences().filter((s) => countsTowardAllowance(s, DEMO_NOW_MS));

// --- Kids --------------------------------------------------------------------

interface KidMeta {
  activity: ChildActivity;
  licence: ChildLicence;
}
const KID_META: Record<string, KidMeta> = {
  "child-3f1a": { activity: "active", licence: "licensed" },
  "child-9c2b": { activity: "active", licence: "paid" },
  "child-7e44": { activity: "unknown", licence: "licensed" },
  "child-1d80": { activity: "active", licence: "free" },
  "child-5a6f": { activity: "inactive", licence: "free" },
  "child-2b9e": { activity: "inactive", licence: "free" },
  // Noah: the demo caregiver's own child, on the free version, so Assign
  // practice shows Premium games locked for him (child_access answers none).
  "child-4c7d": { activity: "active", licence: "free" },
};

// --- Organisation consent + assigned staff (migration 0012) --------------------
//
// Which families have shared their child with the ORGANISATION (consent at
// sign-in), and which of its staff the admin has assigned. Keyed by the opaque
// ref, exactly like the live RPCs. Staff are the fixture members.

const sharedWithOrg = new Set<string>([
  ...["child-3f1a", "child-9c2b", "child-1d80", "child-4c7d"].map(fixtureChildRef),
  "A7C2E9",
]);

const staffStore = new Map<string, OrgChildStaff[]>([
  [fixtureChildRef("child-3f1a"), [{ accountId: "acc-lena", email: "lena@brightsteps.org", role: "teacher", assignedAt: at(-60) }]],
  [
    fixtureChildRef("child-9c2b"),
    [
      { accountId: "acc-aisha", email: "aisha@brightsteps.org", role: "therapist", assignedAt: at(-40) },
      { accountId: "acc-david", email: "david@brightsteps.org", role: "teacher", assignedAt: at(-12) },
    ],
  ],
]);

const staffOf = (ref: string) => staffStore.get(ref) ?? [];

/** Premium for the demo's children, the way child_access answers it. */
const DEMO_ACCESS: Record<string, ChildAccess> = {
  "child-3f1a": { premium: true, source: "licence" },
  "child-9c2b": { premium: true, source: "store" },
  "child-7e44": { premium: true, source: "licence" },
  "child-4c7d": { premium: false, source: "none" },
};

/** The game_tier rule (an unknown game is not free), from the generated registry. */
const isFreeGame = (id: string) => BUILT_GAMES.find((g) => g.id === id)?.free === true;

const archived = new Set<string>([fixtureChildRef("child-2b9e")]);
const ended = new Set<string>();

function linkStatus(links: typeof childLinks): SharingStatus {
  if (links.some((l) => l.consentId && l.scope.length > 0)) return "active";
  if (links.some((l) => l.consentId && l.scope.length === 0)) return "revoked";
  return "pending";
}

function centreKids(): OrgChildEntry[] {
  const byChild = new Map<string, typeof childLinks>();
  for (const l of childLinks) {
    if (l.relation !== "viewer") continue;
    byChild.set(l.childId, [...(byChild.get(l.childId) ?? []), l]);
  }
  return [...byChild.entries()]
    .map(([childId, links]) => {
      const ref = fixtureChildRef(childId);
      const meta = KID_META[childId] ?? { activity: "unknown", licence: "free" };
      return {
        childRef: ref,
        supportedBy: links
          .map((l) => accounts.find((a) => a.id === l.accountId)?.email)
          .filter((e): e is string => !!e)
          .sort(),
        roles: [...new Set(links.map((l) => (l.role ?? "other") as LinkRole))],
        sharingStatus: linkStatus(links),
        activity: meta.activity,
        licence: meta.licence,
        archived: archived.has(ref),
        linkedSince: links.map((l) => l.createdAt ?? null).filter((d): d is string => !!d).sort()[0] ?? null,
        consent: sharedWithOrg.has(ref) ? "shared" : "not_shared",
        staffCount: staffOf(ref).length,
      } satisfies OrgChildEntry;
    })
    .filter((k) => !ended.has(k.childRef));
}

const individualKidsBase: Omit<OrgChildEntry, "consent" | "staffCount">[] = [
  { childRef: "A7C2E9", supportedBy: ["dr.aisyah@example.com"], roles: ["therapist"], sharingStatus: "active", activity: "active", licence: "paid", archived: false, linkedSince: at(-90) },
  { childRef: "4B19D0", supportedBy: ["dr.aisyah@example.com"], roles: ["therapist"], sharingStatus: "active", activity: "inactive", licence: "free", archived: false, linkedSince: at(-150) },
  { childRef: "E03F5A", supportedBy: ["dr.aisyah@example.com"], roles: ["therapist"], sharingStatus: "pending", activity: "unknown", licence: "free", archived: false, linkedSince: at(-3) },
];

function demoKids(): OrgChildEntry[] {
  if (getDemoVariant() === "individual") {
    return individualKidsBase
      .filter((k) => !ended.has(k.childRef))
      .map((k) => ({
        ...k,
        archived: archived.has(k.childRef),
        consent: sharedWithOrg.has(k.childRef) ? ("shared" as const) : ("not_shared" as const),
        staffCount: staffOf(k.childRef).length,
      }));
  }
  return centreKids();
}

// --- Invoices ----------------------------------------------------------------

function monthStart(offset: number): Date {
  const d = new Date(DEMO_NOW_MS);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + offset, 1));
}
const iso = (d: Date) => d.toISOString().slice(0, 10);
const monthEnd = (offset: number) => new Date(monthStart(offset + 1).getTime() - DAY);
const ym = (d: Date) => iso(d).slice(0, 7).replace("-", "");

const round2 = (n: number) => Math.round(n * 100) / 100;

/** app_invoice_amounts: subtotal and SST rounded to the sen, SST on the subtotal. */
function invoiceAmounts(subtotal: number, sstRate: number): { subtotal: number; sstAmount: number; total: number } {
  const sub = round2(subtotal);
  const sst = round2((sub * sstRate) / 100);
  return { subtotal: sub, sstAmount: sst, total: round2(sub + sst) };
}

// --- Revenue share (migration 0009) ------------------------------------------
//
// The Malaysian default: the family pays the ORGANISATION RM40 a month, the
// organisation keeps RM10 and Zooylo invoices it RM30 per licence-month plus 8%
// SST. Little Lanterns shows a different agreement with no SST; the individual
// practitioner has no licences at all.
interface DemoShare {
  retail: number;
  sstRate: number;
}
const demoShare = new Map<string, DemoShare>();

function shareOf(orgId: string): DemoShare {
  return demoShare.get(orgId) ?? { retail: 0, sstRate: 8 };
}

function invoiceFor(orgId: string, offset: number, lm: number, price: number, status: Invoice["status"], suffix: string): Invoice {
  const start = monthStart(offset);
  const rate = shareOf(orgId).sstRate;
  const a = invoiceAmounts(lm * price, rate);
  const issued = new Date(monthStart(offset + 1).getTime() + 2 * DAY);
  return {
    id: `inv-${orgId}-${suffix}`,
    orgId,
    number: `INV-${ym(monthStart(offset + 1))}-${suffix}`,
    periodStart: iso(start),
    periodEnd: iso(monthEnd(offset)),
    licenceMonths: lm,
    unitPrice: price,
    subtotal: a.subtotal,
    sstRate: rate,
    sstAmount: a.sstAmount,
    amount: a.total,
    currency: "MYR",
    status,
    issuedAt: issued.toISOString(),
    dueAt: iso(new Date(issued.getTime() + 30 * DAY)),
    paidAt: status === "paid" ? new Date(issued.getTime() + 12 * DAY).toISOString() : null,
    note: null,
  };
}

demoShare.set(DEMO_ORG_ID, { retail: 40, sstRate: 8 });
demoShare.set(INDIVIDUAL_ORG_ID, { retail: 0, sstRate: 8 });
demoShare.set(SUSPENDED_ORG_ID, { retail: 35, sstRate: 0 });

const invoices: Invoice[] = [
  invoiceFor(DEMO_ORG_ID, -1, 2, 30, "issued", "7F3A1"),
  invoiceFor(DEMO_ORG_ID, -2, 1.94, 30, "paid", "2C9B4"),
  invoiceFor(DEMO_ORG_ID, -3, 1.2, 30, "paid", "91DE0"),
  { ...invoiceFor(DEMO_ORG_ID, -4, 0.5, 30, "void", "5A0C7"), note: "Replaced by a corrected invoice." },
  invoiceFor(SUSPENDED_ORG_ID, -1, 4, 25, "issued", "B44E2"),
  invoiceFor(SUSPENDED_ORG_ID, -2, 4, 25, "issued", "C1F09"),
];

const invoicesFor = (orgId: string) =>
  invoices
    .filter((i) => i.orgId === orgId && i.status !== "draft")
    .sort((a, b) => b.periodStart.localeCompare(a.periodStart));

// --- Console -----------------------------------------------------------------

const consoleOrgs: ConsoleOrg[] = [
  { orgId: DEMO_ORG_ID, name: "Bright Steps Centre", kind: "organisation", type: "centre", status: "active", createdAt: "2026-01-15T08:00:00.000Z", adminEmails: ["marcus@brightsteps.org"], members: 6, familyAllowance: 5, licencesLive: 3, familyPriceMonthly: 30, currency: "MYR", outstanding: 64.8 },
  { orgId: INDIVIDUAL_ORG_ID, name: "Dr Aisyah, speech practice", kind: "individual", type: "clinic", status: "active", createdAt: "2026-05-04T08:00:00.000Z", adminEmails: ["dr.aisyah@example.com"], members: 1, familyAllowance: 0, licencesLive: 0, familyPriceMonthly: 0, currency: "MYR", outstanding: 0 },
  { orgId: SUSPENDED_ORG_ID, name: "Little Lanterns School", kind: "organisation", type: "school", status: "suspended", createdAt: "2026-02-20T08:00:00.000Z", adminEmails: ["office@littlelanterns.example.com"], members: 4, familyAllowance: 10, licencesLive: 4, familyPriceMonthly: 25, currency: "MYR", outstanding: 200 },
];

const accessCodes: AccessCode[] = [
  { code: "ZOO-4F7A21C9", label: "Sunrise therapy centre pilot", plan: "Family licences", kind: "organisation", seatsTotal: 8, familyAllowance: 20, familyPriceMonthly: 30, currency: "MYR", maxUses: 1, uses: 0, expiresAt: at(30), createdAt: at(-2) },
  { code: "ZOO-B03E77D1", label: "Independent OT, no licences", plan: "Family licences", kind: "individual", seatsTotal: 1, familyAllowance: 0, familyPriceMonthly: 0, currency: "MYR", maxUses: 1, uses: 1, expiresAt: null, createdAt: at(-40) },
  { code: "ZOO-9C1D0E42", label: "Bright Steps Centre", plan: "Family licences", kind: "organisation", seatsTotal: 12, familyAllowance: 5, familyPriceMonthly: 30, currency: "MYR", maxUses: 1, uses: 1, expiresAt: null, createdAt: "2026-01-14T08:00:00.000Z" },
];

const licenceTerms = new Map<string, { seats: number | null; billingEmail: string | null; contractEnd: string | null }>();

let seq = 0;
const nextId = (p: string) => `${p}-demo-${++seq}`;

function codeFor(n: number): string {
  // Deterministic, code-shaped, never a real secret.
  return `ZOO-${((n * 2654435761) >>> 0).toString(16).toUpperCase().padStart(8, "0").slice(0, 8)}`;
}

// --- Practice ideas, grouped (migration 20261023000010) ----------------------
//
// The demo's signed-in caregiver is acc-lena (FIXTURE_ACCOUNT_ID), so `fromMe`
// is true for her rows. Older single suggestions keep a null batch id, exactly
// as rows written before the migration do; the extra rows below are a group of
// three from another helper and a pair Lena sent herself.

const DEMO_VIEWER = "acc-lena";

function senderOf(childId: string, accountId: string) {
  const link = childLinks.find((l) => l.childId === childId && l.accountId === accountId);
  const acct = accounts.find((a) => a.id === accountId);
  return {
    fromMe: accountId === DEMO_VIEWER,
    senderRole: link?.role ?? null,
    senderLabel: link?.label ?? null,
    senderOrg: acct?.orgId ? organisation.name : null,
  };
}

const suggestionStore: ChildSuggestion[] = [
  ...fixtureAssignments.map(
    (a): ChildSuggestion => ({
      id: a.id,
      batchId: null,
      target: a.target,
      note: a.note ?? null,
      status: a.status,
      seenAt: a.seenAt ?? null,
      sentAt: a.at,
      ...senderOf(a.childId, a.assignedByAccountId),
    }),
  ),
  // Noah (Lena's own child): three games together from his therapist, this week.
  // On a child she OWNS, Lena sees every helper's ideas; on a child she only
  // helps with (Amira) she sees just her own (migration 0011).
  ...(
    [
      ["giraffes-spot-it", "explored", -4],
      ["parrots-word-zoo", "assigned", -4],
      ["monkeys-sort-yard", "assigned", null],
    ] as const
  ).map(
    ([target, status, seen], i): ChildSuggestion & { childId: string } => ({
      childId: "child-4c7d",
      id: `sugg-noah-t${i}`,
      batchId: "batch-demo-noah-t",
      target,
      note: "This week, try these three. A few minutes each is plenty, and stopping early is fine.",
      status,
      seenAt: seen === null ? null : at(seen),
      sentAt: at(-5),
      fromMe: false,
      senderRole: "therapist",
      senderLabel: "Ms Aisyah",
      senderOrg: organisation.name,
    }),
  ),
  // Amira: a pair Lena sent a month ago, both played.
  ...(["turtles-feeling-corner", "koalas-morning"] as const).map(
    (target, i): ChildSuggestion & { childId: string } => ({
      childId: "child-3f1a",
      id: `sugg-amira-b${i}`,
      batchId: "batch-demo-amira-b",
      target,
      note: null,
      status: "explored",
      seenAt: at(-24),
      sentAt: at(-25),
      ...senderOf("child-3f1a", DEMO_VIEWER),
    }),
  ),
  // Noah (Lena's own child): a pair she sent, not opened yet.
  ...(["penguins-count-pool", "monkeys-memory-match"] as const).map(
    (target, i): ChildSuggestion & { childId: string } => ({
      childId: "child-4c7d",
      id: `sugg-noah-a${i}`,
      batchId: "batch-demo-noah-a",
      target,
      note: "Two short games for the weekend.",
      status: "assigned",
      seenAt: null,
      sentAt: at(-1),
      ...senderOf("child-4c7d", DEMO_VIEWER),
    }),
  ),
];

/** Which child each demo row belongs to (ChildSuggestion itself carries no child id). */
const suggestionChild = new Map<string, string>([
  ...fixtureAssignments.map((a) => [a.id, a.childId] as [string, string]),
  ...(suggestionStore as (ChildSuggestion & { childId?: string })[])
    .filter((r) => r.childId)
    .map((r) => [r.id, r.childId!] as [string, string]),
]);

// --- The methods -------------------------------------------------------------

type DemoMethods = Pick<
  PortalDataSource,
  | "orgProfile"
  | "checkFamilyLicence"
  | "extendSponsorship"
  | "orgBillingSummary"
  | "listInvoices"
  | "orgChildren"
  | "setOrgChildArchived"
  | "endOrgSupport"
  | "orgAnalytics"
  | "amIPlatformAdmin"
  | "consoleOrgs"
  | "consoleAccessCodes"
  | "consoleCreateAccessCode"
  | "consoleLicenceTerms"
  | "consoleSetLicence"
  | "consoleSetOrgStatus"
  | "consolePreviewInvoice"
  | "consoleIssueInvoice"
  | "consoleSetInvoiceStatus"
  | "setLinkDetails"
  | "childAdults"
  | "homeNotes"
  | "createSuggestions"
  | "childSuggestions"
  | "grantSponsorshipsBulk"
  | "setSponsorshipEndDates"
  | "changeSponsorshipEmail"
  | "orgChildStaff"
  | "assignChildStaff"
  | "unassignChildStaff"
  | "childAccess"
>;

const refuse = (fn: string, message: string, code = "P0001") => Promise.reject(new PortalWriteError(fn, message, code));

/** Whether the demo account owns this child (the fixtures' owner, like app_owns_child). */
function demoOwnsChild(childId: string): boolean {
  return fixtureChildren.some((c) => c.id === childId && c.ownerAccountId === FIXTURE_ACCOUNT_ID);
}

export const demoBillingAndConsole: DemoMethods = {
  orgProfile: () => {
    const v = getDemoVariant();
    const profile: OrgProfile = {
      kind: v === "individual" ? "individual" : "organisation",
      status: v === "suspended" ? "suspended" : "active",
    };
    return Promise.resolve(profile);
  },

  checkFamilyLicence: (_orgId, email) => {
    const e = email.trim().toLowerCase();
    const remaining = Math.max(demoAllowance() - live().length, 0);
    let status: FamilyLicenceCheck["status"];
    if (getDemoVariant() === "suspended") status = "suspended";
    else if ((DEMO_PAID_EMAILS as readonly string[]).includes(e)) status = "paid";
    else if (live().some((s) => s.email.toLowerCase() === e)) status = "licensed";
    else if (demoAllowance() === 0) status = "no_allowance";
    else if (remaining === 0) status = "full";
    else status = "available";
    return Promise.resolve({ status, remaining });
  },

  extendSponsorship: (id, endsAt) => {
    const row = demoLicences().find((s) => s.id === id);
    if (row) row.endsAt = endsAt;
    return Promise.resolve();
  },

  orgBillingSummary: (orgId) => {
    // The console asks about other organisations: answer from their console row.
    const other = consoleOrgs.find((o) => o.orgId === orgId && orgId !== DEMO_ORG_ID);
    if (other) {
      const sh = shareOf(orgId);
      const amt = (n: number) => invoiceAmounts(n * other.familyPriceMonthly, sh.sstRate).total;
      const outstandingOther = invoicesFor(orgId).filter((i) => i.status === "issued").reduce((sum, i) => sum + i.amount, 0);
      return Promise.resolve({
        currency: other.currency,
        familyPriceMonthly: other.familyPriceMonthly,
        familyRetailPriceMonthly: sh.retail,
        orgShareMonthly: Math.max(sh.retail - other.familyPriceMonthly, 0),
        sstRate: sh.sstRate,
        familyAllowance: other.familyAllowance,
        licencesLive: other.licencesLive,
        licencesClaimed: other.licencesLive,
        licencesPending: 0,
        monthToDateLicenceMonths: 0,
        monthToDateAmount: 0,
        projectedMonthAmount: amt(other.licencesLive),
        lastMonthLicenceMonths: other.licencesLive,
        lastMonthAmount: amt(other.licencesLive),
        outstandingAmount: Math.round(outstandingOther * 100) / 100,
        orgStatus: other.status,
        contractStart: other.createdAt.slice(0, 10),
        contractEnd: null,
      } satisfies BillingSummary);
    }
    const l = live();
    const claimed = l.filter((s) => s.claimedAt).length;
    const price = demoPrice();
    const v = getDemoVariant();
    const sh = shareOf(demoOrgId());
    const total = (lm: number) => invoiceAmounts(lm * price, sh.sstRate).total;
    const mtd = v === "individual" ? 0 : Math.round(claimed * (new Date(DEMO_NOW_MS).getUTCDate() / 30.4375) * 100) / 100;
    const lastLm = v === "individual" ? 0 : 2;
    const outstanding = invoicesFor(demoOrgId())
      .filter((i) => i.status === "issued")
      .reduce((sum, i) => sum + i.amount, 0);
    const summary: BillingSummary = {
      currency: "MYR",
      familyPriceMonthly: price,
      familyRetailPriceMonthly: sh.retail,
      orgShareMonthly: Math.max(sh.retail - price, 0),
      sstRate: sh.sstRate,
      familyAllowance: demoAllowance(),
      licencesLive: l.length,
      licencesClaimed: claimed,
      licencesPending: l.length - claimed,
      monthToDateLicenceMonths: mtd,
      monthToDateAmount: total(mtd),
      projectedMonthAmount: total(claimed),
      lastMonthLicenceMonths: lastLm,
      lastMonthAmount: total(lastLm),
      outstandingAmount: Math.round(outstanding * 100) / 100,
      orgStatus: v === "suspended" ? "suspended" : "active",
      contractStart: v === "individual" ? day(-170) : "2026-01-15",
      contractEnd: v === "individual" ? null : day(270),
    };
    return Promise.resolve(summary);
  },

  listInvoices: (orgId) => {
    // The admin screens pass their own org id; the demo maps the demo org onto
    // whichever variant is showing.
    const id = orgId === DEMO_ORG_ID || orgId === "org-brightsteps" ? demoOrgId() : orgId;
    return Promise.resolve(invoicesFor(id).map((i) => ({ ...i })));
  },

  orgChildren: () => Promise.resolve(demoKids().map((k) => ({ ...k }))),

  setOrgChildArchived: (_orgId, ref, isArchived) => {
    if (isArchived) archived.add(ref);
    else archived.delete(ref);
    return Promise.resolve();
  },

  endOrgSupport: (_orgId, ref) => {
    const kid = demoKids().find((k) => k.childRef === ref);
    ended.add(ref);
    archived.delete(ref);
    // end_org_support also ends the family's consent to the organisation.
    sharedWithOrg.delete(ref);
    staffStore.delete(ref);
    return Promise.resolve(kid ? kid.supportedBy.length : 0);
  },

  orgAnalytics: () => {
    const v = getDemoVariant();
    const kids = demoKids();
    const l = live();
    const months = [-5, -4, -3, -2, -1, 0].map((o) => iso(monthStart(o)).slice(0, 7));
    const lm = v === "individual" ? [0, 0, 0, 0, 0, 0] : [0.5, 1.2, 1.94, 2, 2, Math.round(l.filter((s) => s.claimedAt).length * (new Date(DEMO_NOW_MS).getUTCDate() / 30.4375) * 100) / 100];
    const a: OrgAnalytics = {
      members: v === "individual" ? 1 : 6,
      admins: 1,
      childrenLinked: kids.length,
      childrenActive30d: kids.filter((k) => k.activity === "active").length,
      childrenArchived: kids.filter((k) => k.archived).length,
      licencesLive: l.length,
      licencesClaimed: l.filter((s) => s.claimedAt).length,
      licencesEnding30d: l.filter((s) => Date.parse(s.endsAt) < DEMO_NOW_MS + 30 * DAY).length,
      suggestions30d: v === "individual" ? 4 : 9,
      suggestionsSeen30d: v === "individual" ? 3 : 7,
      suggestionsPlayed30d: v === "individual" ? 2 : 5,
      licenceMonthsByMonth: months.map((month, i) => ({ month, licenceMonths: lm[i] })),
    };
    return Promise.resolve(a);
  },

  amIPlatformAdmin: () => Promise.resolve(true),

  consoleOrgs: () =>
    Promise.resolve(
      consoleOrgs.map((o) => ({
        ...o,
        outstanding:
          Math.round(
            invoices
              .filter((i) => i.orgId === o.orgId && i.status === "issued")
              .reduce((s, i) => s + i.amount, 0) * 100,
          ) / 100,
      })),
    ),

  consoleAccessCodes: () => Promise.resolve(accessCodes.map((c) => ({ ...c }))),

  consoleCreateAccessCode: (input) => {
    const retail = Math.max(input.familyRetailPrice, 0);
    if (retail > 0 && input.familyPrice > retail) {
      // console_create_access_code refuses this with P0001.
      return Promise.reject(Object.assign(new Error("share above retail"), { code: "P0001" }));
    }
    const code = codeFor(accessCodes.length + 7);
    accessCodes.unshift({
      code,
      label: input.label,
      plan: "Family licences",
      kind: input.kind,
      seatsTotal: Math.max(input.seats ?? (input.kind === "individual" ? 1 : 5), 1),
      familyAllowance: Math.max(input.familyAllowance, 0),
      familyPriceMonthly: Math.max(input.familyPrice, 0),
      currency: (input.currency || "MYR").toUpperCase(),
      maxUses: Math.max(input.maxUses, 1),
      uses: 0,
      expiresAt: input.expiresAt,
      createdAt: new Date().toISOString(),
    });
    return Promise.resolve(code);
  },

  consoleLicenceTerms: (orgId) => {
    const o = consoleOrgs.find((x) => x.orgId === orgId);
    const t = licenceTerms.get(orgId);
    const sh = shareOf(orgId);
    return Promise.resolve({
      seats: t?.seats ?? (o?.kind === "individual" ? 1 : 12),
      familyAllowance: o?.familyAllowance ?? 0,
      familyPrice: o?.familyPriceMonthly ?? 0,
      familyRetailPrice: sh.retail,
      sstRate: sh.sstRate,
      currency: o?.currency ?? "MYR",
      billingEmail: t?.billingEmail ?? (o?.adminEmails[0] ?? null),
      contractEnd: t ? t.contractEnd : o?.kind === "individual" ? null : day(270),
    });
  },

  consoleSetLicence: (orgId, terms) => {
    const o = consoleOrgs.find((x) => x.orgId === orgId);
    if (o) {
      if (terms.familyAllowance !== null) o.familyAllowance = terms.familyAllowance;
      if (terms.familyPrice !== null) o.familyPriceMonthly = terms.familyPrice;
      if (terms.currency) o.currency = terms.currency.toUpperCase();
    }
    const sh = shareOf(orgId);
    demoShare.set(orgId, {
      retail: terms.familyRetailPrice ?? sh.retail,
      sstRate: terms.sstRate ?? sh.sstRate,
    });
    licenceTerms.set(orgId, { seats: terms.seats, billingEmail: terms.billingEmail, contractEnd: terms.contractEnd });
    return Promise.resolve();
  },

  consoleSetOrgStatus: (orgId, status, endLicences) => {
    const o = consoleOrgs.find((x) => x.orgId === orgId);
    if (o) {
      o.status = status;
      if (endLicences) o.licencesLive = 0;
    }
    return Promise.resolve();
  },

  consolePreviewInvoice: (orgId, periodStart, periodEnd) => {
    const o = consoleOrgs.find((x) => x.orgId === orgId);
    if (!o) return Promise.resolve(null);
    const days = (Date.parse(periodEnd) - Date.parse(periodStart)) / DAY + 1;
    const lm = Math.round(((o.licencesLive * days) / 30.4375) * 100) / 100;
    const rate = shareOf(orgId).sstRate;
    const a = invoiceAmounts(lm * o.familyPriceMonthly, rate);
    return Promise.resolve({
      licenceMonths: lm,
      unitPrice: o.familyPriceMonthly,
      subtotal: a.subtotal,
      sstRate: rate,
      sstAmount: a.sstAmount,
      amount: a.total,
      currency: o.currency,
    });
  },

  consoleIssueInvoice: (orgId, input) => {
    const o = consoleOrgs.find((x) => x.orgId === orgId);
    const price = o?.familyPriceMonthly ?? 0;
    const days = (Date.parse(input.periodEnd) - Date.parse(input.periodStart)) / DAY + 1;
    const lm = Math.round(((o?.licencesLive ?? 0) * days) / 30.4375 * 100) / 100;
    const id = nextId("inv");
    const rate = shareOf(orgId).sstRate;
    // An entered amount replaces the SUBTOTAL; SST is always added on top.
    const a = invoiceAmounts(input.amount ?? lm * price, rate);
    invoices.push({
      id,
      orgId,
      number: `INV-${ym(new Date())}-${id.slice(-3).toUpperCase().padStart(5, "D")}`,
      periodStart: input.periodStart,
      periodEnd: input.periodEnd,
      licenceMonths: lm,
      unitPrice: price,
      subtotal: a.subtotal,
      sstRate: rate,
      sstAmount: a.sstAmount,
      amount: a.total,
      currency: o?.currency ?? "MYR",
      status: "issued",
      issuedAt: new Date().toISOString(),
      dueAt: input.due,
      paidAt: null,
      note: input.note,
    });
    return Promise.resolve(id);
  },

  consoleSetInvoiceStatus: (invoiceId, status) => {
    const inv = invoices.find((i) => i.id === invoiceId);
    if (inv) {
      inv.status = status;
      inv.paidAt = status === "paid" ? new Date().toISOString() : null;
    }
    return Promise.resolve();
  },

  setLinkDetails: () => Promise.resolve(),

  childAdults: (childId) => {
    const out: ChildAdult[] = childLinks
      .filter((l) => l.childId === childId && l.relation === "viewer")
      .map((l) => ({
        accountId: l.accountId,
        email: accounts.find((a) => a.id === l.accountId)?.email ?? l.accountId,
        role: l.role ?? null,
        label: l.label ?? null,
        scope: l.scope,
        status: linkStatus([l]),
        orgName: "Bright Steps Centre",
        linkedAt: l.createdAt ?? null,
      }));
    return Promise.resolve(out);
  },

  homeNotes: () => Promise.resolve(fixtureHomeNotes.map((n) => ({ ...n }))),

  createSuggestions: (childId, targets, note) => {
    // The same rules create_suggestions enforces: trimmed, de-duplicated,
    // order kept, 1–3 games, a note of at most 280 characters.
    const clean = [...new Set(targets.map((t) => t.trim()).filter(Boolean))];
    if (clean.length === 0) {
      return Promise.reject(new PortalWriteError("create_suggestions", "choose at least one game", "P0001"));
    }
    if (clean.length > SUGGESTION_GAMES_MAX) {
      return Promise.reject(new PortalWriteError("create_suggestions", "choose up to 3 games at a time", "P0001"));
    }
    // 0012: no Premium game for a family without Premium.
    const access = DEMO_ACCESS[childId] ?? { premium: true, source: "licence" };
    if (!access.premium && clean.some((t) => !isFreeGame(t))) {
      return refuse("create_suggestions", "premium game for a family without Premium");
    }
    const batchId = nextId("batch");
    const sentAt = new Date().toISOString();
    const cleanNote = normaliseNote(note);
    clean.forEach((target) => {
      const id = nextId("sugg");
      suggestionChild.set(id, childId);
      suggestionStore.push({
        id,
        batchId,
        target,
        note: cleanNote,
        status: "assigned",
        seenAt: null,
        sentAt,
        ...senderOf(childId, DEMO_VIEWER),
      });
    });
    return Promise.resolve(batchId);
  },

  childSuggestions: (childId) =>
    Promise.resolve(
      suggestionStore
        .filter((r) => suggestionChild.get(r.id) === childId)
        // Same rule as child_suggestions (0011): the owner sees every idea, a helper only their own.
        .filter((r) => r.fromMe || demoOwnsChild(childId))
        .map(({ id, batchId, target, note, status, seenAt, sentAt, fromMe, senderRole, senderLabel, senderOrg }) => ({
          id,
          batchId,
          target,
          note,
          status,
          seenAt,
          sentAt,
          fromMe,
          senderRole,
          senderLabel,
          senderOrg,
        }))
        .sort((a, b) => (a.sentAt < b.sentAt ? 1 : a.sentAt > b.sentAt ? -1 : a.id.localeCompare(b.id))),
    ),

  // --- Licence tools (migration 0013) ----------------------------------------
  //
  // The same answers grant_family_sponsorships_bulk gives, row by row, against
  // the demo's own licences: a paying family, a family already licensed, a
  // suspended organisation, and an allowance that runs out part-way through.

  grantSponsorshipsBulk: (_orgId, emails, endsAt) => {
    if (emails.length > 2000) return refuse("grant_family_sponsorships_bulk", "upload at most 2000 emails at a time");
    if (!(Date.parse(endsAt) > Date.now())) return refuse("grant_family_sponsorships_bulk", "licences must end in the future");
    const seen = new Set<string>();
    const out: BulkLicenceResult[] = [];
    for (const raw of emails) {
      const email = raw.trim().toLowerCase();
      if (!email || seen.has(email)) continue;
      seen.add(email);
      let status: BulkLicenceResult["status"];
      let sponsorshipId: string | null = null;
      if (!isValidEmail(email)) status = "invalid";
      else if (getDemoVariant() === "suspended") status = "suspended";
      else if ((DEMO_PAID_EMAILS as readonly string[]).includes(email)) status = "paid";
      else if (live().some((s) => s.email.toLowerCase() === email)) status = "already_licensed";
      else if (live().length >= demoAllowance()) status = "full";
      else {
        sponsorshipId = nextId("lic");
        demoLicences().unshift({ id: sponsorshipId, email, endsAt, grantedAt: new Date(DEMO_NOW_MS).toISOString(), claimedAt: null, revokedAt: null });
        status = "licensed";
      }
      out.push({ email, status, sponsorshipId });
    }
    return Promise.resolve(out);
  },

  setSponsorshipEndDates: (_orgId, ids, endsAt) => {
    if (getDemoVariant() === "suspended") return refuse("set_sponsorship_end_dates", "this organisation is suspended: licences cannot be changed");
    if (!(Date.parse(endsAt) > Date.now())) return refuse("set_sponsorship_end_dates", "licences must end in the future");
    const rows = demoLicences().filter((s) => ids.includes(s.id) && !s.revokedAt);
    const before = rows.map((r) => r.endsAt);
    rows.forEach((r) => (r.endsAt = endsAt));
    const used = live().length;
    if (used > demoAllowance()) {
      // The RPC raises and its transaction rolls back: put every date back.
      rows.forEach((r, i) => (r.endsAt = before[i]));
      return refuse("set_sponsorship_end_dates", `family allowance reached (${used} of ${demoAllowance()}): end some licences first`);
    }
    return Promise.resolve(rows.length);
  },

  changeSponsorshipEmail: (id, email) => {
    const row = demoLicences().find((s) => s.id === id && !s.revokedAt);
    if (!row) return refuse("change_sponsorship_email", "no live licence with that id", "P0002");
    const e = email.trim().toLowerCase();
    if (!isValidEmail(e)) return refuse("change_sponsorship_email", "invalid email address");
    if (e === row.email.toLowerCase()) return Promise.resolve(row.id);
    if (live().some((s) => s.email.toLowerCase() === e)) return refuse("change_sponsorship_email", "this family already has a sponsorship");
    if ((DEMO_PAID_EMAILS as readonly string[]).includes(e)) {
      return refuse("change_sponsorship_email", "this family already has Premium through the app store: no licence needed");
    }
    if (!row.claimedAt) {
      row.email = e;
      return Promise.resolve(row.id);
    }
    const now = new Date(DEMO_NOW_MS).toISOString();
    row.revokedAt = now;
    const newId = nextId("lic");
    demoLicences().unshift({ id: newId, email: e, endsAt: row.endsAt, grantedAt: now, claimedAt: null, revokedAt: null });
    return Promise.resolve(newId);
  },

  // --- Organisation consent + staff (migration 0012) --------------------------

  orgChildStaff: (_orgId, ref) => Promise.resolve(staffOf(ref).map((s) => ({ ...s }))),

  assignChildStaff: (_orgId, ref, accountId, role) => {
    if (!sharedWithOrg.has(ref)) return refuse("assign_child_staff", "the family has not shared this child with the organisation");
    const acct = accounts.find((a) => a.id === accountId && a.orgId);
    if (!acct) return refuse("assign_child_staff", "that person is not a member of this organisation");
    const existing = staffOf(ref).find((s) => s.accountId === accountId);
    const list = staffOf(ref).filter((s) => s.accountId !== accountId);
    list.push({ accountId, email: acct.email, role, assignedAt: existing?.assignedAt ?? new Date().toISOString() });
    staffStore.set(ref, list.sort((a, b) => a.email.localeCompare(b.email)));
    return Promise.resolve();
  },

  unassignChildStaff: (_orgId, ref, accountId) => {
    staffStore.set(ref, staffOf(ref).filter((s) => s.accountId !== accountId));
    return Promise.resolve();
  },

  childAccess: (childId) => Promise.resolve({ ...(DEMO_ACCESS[childId] ?? { premium: true, source: "licence" }) }),
};
